import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Logo from "./components/Logo";
import MainNav from "./components/MainNav";
import ContactButton from "./components/ContactButton";
import LoginButton from "./components/LoginButton";
import MenuToggle from "./components/MenuToggle";

const DESKTOP_NAV_MIN = 961;

const Navbar = memo(() => {
  const { t } = useTranslation();
  const [elevated, setElevated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((open) => !open);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    let rafId = 0;
    const ELEVATE_AFTER = 44;
    const DE_ELEVATE_BELOW = 10;

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const y = window.scrollY;
        setElevated((prev) =>
          prev ? y > DE_ELEVATE_BELOW : y > ELEVATE_AFTER
        );
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    document.body.dataset.headerCondensed = elevated ? "true" : "false";
    return () => {
      delete document.body.dataset.headerCondensed;
    };
  }, [elevated]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add("mobile-menu-active");
    } else {
      document.body.classList.remove("mobile-menu-active");
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    return () => document.body.classList.remove("mobile-menu-active");
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth >= DESKTOP_NAV_MIN) setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <header
      className={`header--new${elevated ? " header--new--elevated" : ""}`}
    >
      <a href="#main-content" className="vh-skip-main">
        {t("skip.main")}
      </a>
      <div className="container-n">
        <div className="header--new__row">
          <Logo onNavigate={closeMobileMenu} />
          <div className="header--new__info">
            <MainNav
              onNavigate={closeMobileMenu}
              mobileDrawerOpen={mobileMenuOpen}
            />
            <LanguageSwitcher onNavigate={closeMobileMenu} />
            <LoginButton desktop />
            <ContactButton desktop />
          </div>
          <div className="header--new__mobile-actions">
            <LoginButton mobile onNavigate={closeMobileMenu} />
            <ContactButton mobile onNavigate={closeMobileMenu} />
          </div>
          <MenuToggle
            expanded={mobileMenuOpen}
            onToggle={toggleMobileMenu}
          />
        </div>
      </div>
    </header>
  );
});

export default Navbar;
